import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { useLandingConfig } from "@/hooks/useLandingConfig";
import SafeImg from "@/components/landing/SafeImg";

/**
 * LandingCourses — foto e descrizione dinamiche:
 *   • foto: da cfg.images.course_<slug>_photo (editabile dal CMS)
 *   • descrizione: dal DB corso (super_admin → tab Corsi → Modifica);
 *     fallback ai valori di default se il DB/CMS non ha dati per uno slot.
 *   Nessun prezzo mostrato: la landing serve alla scoperta, il prezzo si vede
 *   in area cliente al momento della prenotazione.
 */
const COURSE_SLOTS = [
  { slug: "matwork",    title: "Matwork & Props",     hint: "Classi da 6",
    default_description: "Pilates a corpo libero con piccoli attrezzi. Classi contenute per un lavoro accurato sulla postura, la respirazione e il core.",
    default_visual: "https://images.unsplash.com/photo-1552196563-55cd4e45efb3?auto=format&fit=crop&w=1100&q=80" },
  { slug: "reformer",   title: "Reformer",            hint: "Classi da 2",
    default_description: "Il vero Pilates sul Reformer: molle progressive, movimento fluido e allineamento millimetrico per un lavoro di forza, controllo e mobilità.",
    default_visual: "https://customer-assets-rejwkqb3.emergentagent.net/job_spec-builder-103/artifacts/2ikxgavd_WhatsApp%20Image%202026-07-22%20at%2023.00.58%20%282%29.jpeg" },
  { slug: "aereo",      title: "Pilates Aereo",       hint: "Classi da 4",
    default_description: "Sospensione, forza e leggerezza: il corpo si allunga e si libera nel tessuto aereo per una pratica unica ed emozionante.",
    default_visual: "https://images.unsplash.com/photo-1591291621060-89264f8f6b8c?auto=format&fit=crop&w=1100&q=80" },
  { slug: "gravidanza", title: "Pilates Gravidanza",  hint: "In studio + Online",
    default_description: "Un allenamento dolce e sicuro pensato per le future mamme, per prevenire i fastidi tipici della gravidanza e arrivare preparate al parto.",
    default_visual: "https://thb.tildacdn.net/tild6565-6336-4534-a236-373532663434/-/resize/1200x/DSC06065.jpg" },
];

const EXTRA_COURSES = [
  { slug: "barre",       title: "Pilates Barre",       hint: "Classi da 6",
    default_description: "L'energia della danza classica unita al controllo del Pilates. Un lavoro totale su gambe, glutei e postura." },
  { slug: "cadillac",    title: "Cadillac",            hint: "Classi da 2",
    default_description: "Il grande attrezzo per un Pilates completo: trazioni, spinte e stretching guidato per liberare la colonna e ritrovare mobilità." },
  { slug: "senior",      title: "Senior Over 65",      hint: "Sconto 50%",
    default_description: "Un percorso dedicato agli over 65: movimenti dolci, respirazione consapevole e recupero funzionale per mantenersi attivi e sicuri." },
  { slug: "individuale", title: "Pilates Individuale", hint: "One-to-one",
    default_description: "Un'insegnante tutta per te: massima attenzione, obiettivi personalizzati e rispetto assoluto dei tuoi ritmi." },
];

export default function LandingCourses() {
  const [dbCourses, setDbCourses] = useState([]);
  const { T, cfg } = useLandingConfig();

  const eyebrow = T("texts.courses.eyebrow", "I nostri corsi");
  const titleLine1 = T("texts.courses.title_line1", "Ogni corso è progettato");
  const titleLine2 = T("texts.courses.title_line2", "per portarti benefici.");
  const italicWord = T("texts.courses.title_italic_word", "portarti benefici");
  const extraEyebrow = T("texts.courses.extra_eyebrow", "Anche disponibili");
  const extraTitle = T("texts.courses.extra_title", "Barre · Cadillac · Senior · Individuale");
  const ctaScheduleLabel = T("texts.courses.cta_book_label", "Prenota ora la tua lezione");
  const footnote = T("texts.courses.footnote", "Registrati per prenotare la tua lezione dal palinsesto: gli orari sono aggiornati in tempo reale con le insegnanti disponibili.");

  useEffect(() => {
    api.get("/courses").then(r => setDbCourses(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  /** Descrizione effettiva per uno slot corso.
   * Priorità:
   *   1. `cfg.texts.courses.<slug>_description` — override CMS Landing (super_admin)
   *   2. `description` del documento corso in DB (editabile da admin corsi)
   *   3. `slot.default_description` — fallback hardcoded.
   */
  const descriptionFor = (slot) => {
    const cmsDesc = cfg?.texts?.courses?.[`${slot.slug}_description`];
    if (cmsDesc && String(cmsDesc).trim()) return cmsDesc;
    const dbCourse = findCourse(slot);
    if (dbCourse?.description && String(dbCourse.description).trim()) return dbCourse.description;
    return slot.default_description;
  };

  /** Immagine da mostrare per uno slot corso.
   * Priorità:
   *   1. `cfg.images.course_<slug>_photo` — override CMS Landing (super_admin)
   *   2. `image_url` del documento corso in DB
   *   3. `slot.default_visual` — fallback hardcoded (usato come `fallback` di SafeImg).
   */
  const imageFor = (slot) => {
    const cmsUrl = cfg?.images?.[`course_${slot.slug}_photo`];
    if (cmsUrl && String(cmsUrl).trim()) return cmsUrl;
    const dbCourse = findCourse(slot);
    if (dbCourse?.image_url && String(dbCourse.image_url).trim()) return dbCourse.image_url;
    return "";
  };

  const renderTitleLine = (line) => {
    if (!italicWord || !line?.toLowerCase().includes(italicWord.toLowerCase())) return line;
    const idx = line.toLowerCase().indexOf(italicWord.toLowerCase());
    return (
      <>
        {line.slice(0, idx)}
        <span className="font-editorial text-mauve">{line.slice(idx, idx + italicWord.length)}</span>
        {line.slice(idx + italicWord.length)}
      </>
    );
  };

  function findCourse(slot) {
    return dbCourses.find(c => (c.name || "").toLowerCase().includes(slot.slug))
      || dbCourses.find(c => (c.name || "").toLowerCase().includes(slot.title.toLowerCase().split(" ")[0]));
  }

  return (
    <section data-testid="landing-courses" id="corsi" className="relative bg-background">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40">
        {/* Header spazioso */}
        <div className="text-center mb-20 md:mb-24">
          <div className="text-[10px] uppercase tracking-[0.42em] text-foreground/50 mb-8 flex items-center justify-center gap-3">
            <span className="w-10 h-px bg-foreground/40" />
            {eyebrow}
            <span className="w-10 h-px bg-foreground/40" />
          </div>
          <h2 className="font-serif-display font-normal text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-3xl mx-auto">
            {renderTitleLine(titleLine1)}
            <br />
            {renderTitleLine(titleLine2)}
          </h2>
        </div>

        {/* Griglia 4 principali — molto respiro */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {COURSE_SLOTS.map((slot) => (
            <article
              key={slot.slug}
              data-testid={`course-card-${slot.slug}`}
              className="group relative"
            >
              <div className="relative aspect-[16/11] overflow-hidden rounded-[2rem]">
                <SafeImg
                  src={imageFor(slot)}
                  fallback={slot.default_visual}
                  alt={slot.title}
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                />
              </div>
              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-[0.32em] text-foreground/50 mb-2">{slot.hint}</div>
                <div className="font-serif-display text-3xl md:text-4xl leading-tight">{slot.title}</div>
                <p
                  className="mt-4 text-base md:text-lg leading-relaxed text-foreground/70 font-light"
                  data-testid={`course-desc-${slot.slug}`}
                >
                  {descriptionFor(slot)}
                </p>
              </div>
            </article>
          ))}
        </div>

        {/* Extra corsi — lista sobria con descrizione, senza prezzi, senza CTA
            (la call to action unica è quella sotto). */}
        <div className="mt-24 border-t border-border/50 pt-16">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.32em] text-foreground/50 mb-3">{extraEyebrow}</div>
            <div className="font-serif-display text-2xl md:text-3xl">{extraTitle}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-5xl mx-auto">
            {EXTRA_COURSES.map((c) => (
              <div
                key={c.slug}
                data-testid={`course-card-${c.slug}`}
                className="text-left p-6 rounded-3xl border border-border/60"
              >
                <div className="text-[10px] uppercase tracking-[0.28em] text-foreground/50 mb-2">{c.hint}</div>
                <div className="font-serif-display text-xl md:text-2xl leading-tight text-foreground">{c.title}</div>
                <p
                  className="mt-3 text-sm md:text-base text-foreground/65 leading-relaxed font-light"
                  data-testid={`course-desc-${c.slug}`}
                >
                  {descriptionFor(c)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA "Prenota ora la tua lezione" → /registrati */}
        <div className="mt-20 text-center">
          <Link
            to="/registrati"
            data-testid="courses-cta-book"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.28em] hover:bg-primary/90 transition-all btn-lift"
          >
            {ctaScheduleLabel}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5}/>
          </Link>
          <p className="mt-6 text-xs text-foreground/50 max-w-md mx-auto leading-relaxed">
            {footnote}
          </p>
        </div>
      </div>
    </section>
  );
}
